// CTW-DURMAH-STUDENT-MANUAL-01:
// Public Student Manual hub. Indexed for SEO — see the matching entries in
// src/lib/layout/routeSurface.ts (PUBLIC_EXACT_PATHS "/manual" AND
// PUBLIC_PREFIXES "/manual/"; both are required, per the /learn precedent).
//
// Member-only sections appear here as locked cards so a signed-out visitor
// can see that the answer exists. Gating is enforced in the section page,
// never here.

import React, { useMemo, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import {
  ArrowRight,
  BookOpen,
  CalendarDays,
  ClipboardCheck,
  Compass,
  FileText,
  GraduationCap,
  Heart,
  LayoutDashboard,
  LifeBuoy,
  Lock,
  Mic,
  Settings,
  ShieldCheck,
  Users,
} from "lucide-react";
import {
  getPopulatedCategories,
  getSectionsInCategory,
  MANUAL_SECTION_COUNT,
  type ManualCategory,
  type ManualSection,
} from "@/content/manual";
import ManualSearch from "@/components/manual/ManualSearch";
import {
  ManualDisclaimer,
  ManualHelpFooter,
} from "@/components/manual/ManualPrimitives";
import { useAuth } from "@/lib/supabase/AuthContext";

const SITE = "https://www.durmah.ai";

const ICONS: Record<ManualCategory["icon"], React.ComponentType<{ className?: string }>> = {
  Compass,
  LayoutDashboard,
  Mic,
  FileText,
  ClipboardCheck,
  CalendarDays,
  BookOpen,
  Heart,
  Users,
  Settings,
  ShieldCheck,
  LifeBuoy,
  GraduationCap,
};

/* ------------------------------------------------------------------ */

function SectionCard({
  section,
  isSignedIn,
}: {
  section: ManualSection;
  isSignedIn: boolean;
}) {
  const gated = section.audience === "member" && !isSignedIn;

  return (
    <Link
      href={`/manual/${section.slug}`}
      prefetch={false}
      className="group flex flex-col h-full bg-white dark:bg-slate-900/50 rounded-2xl border border-gray-100 dark:border-slate-800 p-6 shadow-sm hover:shadow-lg hover:border-indigo-200 dark:hover:border-indigo-500/50 transition-all duration-300 hover:-translate-y-0.5"
    >
      <div className="flex items-center gap-2 mb-3">
        {section.tags.slice(0, 2).map((tag) => (
          <span
            key={tag}
            className="text-[10px] font-black uppercase tracking-tighter px-2 py-0.5 rounded bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-slate-400"
          >
            {tag}
          </span>
        ))}
        {gated ? (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-indigo-500">
            <Lock className="w-2.5 h-2.5" aria-hidden="true" />
            Members
          </span>
        ) : null}
      </div>

      <h3 className="text-base font-bold text-gray-900 dark:text-white mb-2 leading-snug group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
        {section.title}
      </h3>

      <p className="text-sm leading-relaxed text-gray-600 dark:text-slate-400 flex-1">
        {section.summary}
      </p>

      <div className="mt-4 pt-4 border-t border-gray-50 dark:border-slate-800/70 flex items-center justify-between">
        <span className="text-[10px] font-black uppercase tracking-widest text-gray-400">
          {section.readTime}
        </span>
        <span className="text-indigo-600 dark:text-indigo-400 font-black text-xs flex items-center gap-1 group-hover:gap-2 transition-all">
          Read <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
}

/* ------------------------------------------------------------------ */

export default function StudentManualHub() {
  const { user } = useAuth();
  const isSignedIn = Boolean(user);
  const categories = useMemo(() => getPopulatedCategories(), []);
  const [activeCategory, setActiveCategory] = useState<string>("All");

  const canonical = `${SITE}/manual`;
  const title = "Durmah Student Manual: How to Use Durmah | Durmah";
  const description =
    "The complete guide to using Durmah: uploading lectures, planning your year, working through an assignment, getting diagnostic feedback on your own draft, and where Durmah draws the line.";

  const visibleCategories =
    activeCategory === "All"
      ? categories
      : categories.filter((c) => c.id === activeCategory);

  const collectionSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Durmah Student Manual",
    description,
    url: canonical,
    hasPart: categories
      .flatMap((c) => getSectionsInCategory(c.id))
      .map((s) => ({
        "@type": "WebPage",
        name: s.title,
        url: `${SITE}/manual/${s.slug}`,
      })),
  });

  return (
    <div className="bg-white dark:bg-[#020617] min-h-screen flex flex-col pt-32">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link key="route-canonical" rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta key="route-og-url" property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: collectionSchema }}
        />
      </Head>

      <main className="flex-1 py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Hero */}
          <div className="max-w-3xl mb-10">
            <p className="text-xs font-black uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400 mb-4">
              Student Manual
            </p>
            <h1 className="text-4xl lg:text-5xl font-black text-gray-900 dark:text-white leading-tight mb-5">
              Everything Durmah does, and{" "}
              <span className="text-indigo-600 dark:text-indigo-400">
                how to use it
              </span>
              .
            </h1>
            <p className="text-lg leading-relaxed text-gray-600 dark:text-slate-400">
              {MANUAL_SECTION_COUNT} sections covering every part of the
              platform, from your first sign-in to getting diagnostic feedback
              on a draft the night before it is due. Written for students, and
              open to read before you have an account.
            </p>
          </div>

          {/* Search */}
          <div className="max-w-3xl mb-10">
            <ManualSearch isSignedIn={isSignedIn} />
            <p className="mt-3 text-xs text-gray-400 dark:text-slate-600">
              Looking for something specific? Search above, or ask Durmah. It
              answers from this manual and links you to the section.
            </p>
          </div>

          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-12">
            <button
              type="button"
              onClick={() => setActiveCategory("All")}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${
                activeCategory === "All"
                  ? "bg-indigo-600 text-white border-indigo-600 shadow-md"
                  : "bg-white dark:bg-slate-900/50 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-800 hover:border-indigo-300 hover:text-indigo-600"
              }`}
            >
              All sections
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all border ${
                  activeCategory === cat.id
                    ? "bg-indigo-600 text-white border-indigo-600 shadow-md"
                    : "bg-white dark:bg-slate-900/50 text-gray-500 dark:text-slate-400 border-gray-200 dark:border-slate-800 hover:border-indigo-300 hover:text-indigo-600"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* Categories */}
          <div className="space-y-14">
            {visibleCategories.map((category) => {
              const sections = getSectionsInCategory(category.id);
              const Icon = ICONS[category.icon] ?? Compass;

              return (
                <section
                  key={category.id}
                  id={category.id}
                  className="scroll-mt-32"
                >
                  <div className="flex items-start gap-4 mb-6">
                    <div className="w-11 h-11 rounded-2xl bg-indigo-600 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
                        {category.label}
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                        {category.blurb}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {sections.map((section) => (
                      <SectionCard
                        key={section.slug}
                        section={section}
                        isSignedIn={isSignedIn}
                      />
                    ))}
                  </div>
                </section>
              );
            })}
          </div>

          {/* Trial nudge for signed-out visitors */}
          {!isSignedIn ? (
            <div className="mt-16 rounded-3xl bg-gray-900 dark:bg-slate-900 p-10 text-center">
              <h2 className="text-2xl font-bold text-white mb-3">
                The manual is free to read. The workspace takes a minute to
                start.
              </h2>
              <p className="text-sm leading-relaxed text-gray-400 max-w-xl mx-auto mb-6">
                Full access during your trial, no card required, and nothing is
                charged automatically.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  href="/login?intent=signup"
                  prefetch={false}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors"
                >
                  Start a free trial{" "}
                  <ArrowRight className="w-4 h-4" aria-hidden="true" />
                </Link>
                <Link
                  href="/why-durmah"
                  prefetch={false}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-xl border border-white/20 text-white text-sm font-bold hover:border-white/40 transition-colors"
                >
                  Why Durmah
                </Link>
              </div>
            </div>
          ) : null}

          <ManualHelpFooter />
          <ManualDisclaimer />
        </div>
      </main>
    </div>
  );
}
